import React, { FC } from 'react';
import { Modal, Box, SxProps, Theme, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Определение пропсов для компонента FormModal
 */
interface FormModalProps {
  /** Флаг, управляющий открытием/закрытием модального окна. */
  open: boolean;
  /** Функция, вызываемая при запросе на закрытие (например, по клику на фон или Esc). */
  onClose: () => void;
  /** Произвольное содержимое, которое будет отображаться внутри модального окна (например, ваша форма). */
  children: React.ReactNode;
  ratio?: number;
}

/**
 * Переиспользуемый компонент модального окна для произвольной формы.
 */
export const FormModal: FC<FormModalProps> = ({ open, onClose, children, ratio = 0.33 }) => {
  // Стандартные стили для центрирования и адаптивности модального окна
  // Соответствуют стилям из вашего примера
  const modalStyle: SxProps<Theme> = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',

    // width: { xs: '90%', sm: 600 }, // Адаптивная ширина: 90% на мобильных, 600px на больших экранах
    // Логика: на мобильных (xs) всегда почти на весь экран, 
    // на десктопах (sm и выше) — согласно ratio
    width: {
      xs: '95%', 
      sm: `${ratio * 100}vw` 
    },
    
    // Ограничиваем максимальную ширину для сверхшироких мониторов
    maxWidth: '1600px',

    bgcolor: 'background.paper',
    boxShadow: 24,
    p: { xs: 2, sm: 4 }, // Адаптивный padding
    borderRadius: 1,
    maxHeight: '90vh', // Ограничение высоты модального окна
    overflowY: 'auto', // Прокрутка внутри модального окна, если содержимое не помещается
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="form-modal-title" // Для доступности
      aria-describedby="form-modal-description" // Для доступности
      // disableScrollLock // ???
      // disableRestoreFocus // ???
    >
      <Box sx={modalStyle}>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: (theme) => theme.palette.grey[500],
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>
        {/*
          Здесь будет отображаться переданное содержимое (например, ваш OriginForm).
          Вам не нужно дублировать стили Box, они уже встроены в FormModal.
        */}
        {children}
      </Box>
    </Modal>
  );
};